import { useState, type PropsWithChildren } from 'react';
import useEmblaCarousel from 'embla-carousel-react';

import { ChevronLeftSVG } from 'components/iconsSVG/chevronLeft';
import { ProgressiveImg } from 'components/base';
import { useI18n } from 'context/i18n';

import { dataLayerHandleEvent } from 'utils/dataLayers';
import { getDomainValue } from 'utils/domain';
import { Button } from 'components/ui/button';
import { Link } from 'components/ui/link';

import { useCarouselControls } from 'components/ui/carousel-hooks';

type TProductBrandsProps = {
  id: string;
  products?: any[];
  placement?: 'grid' | 'pdp';
  camperlab?: boolean;
  selectBrandProduct?: ((id: string, products: string[]) => void) | undefined;
};

type BrandElementProps = {
  onPress: () => void;
  type: 'link' | 'button';
  href?: string;
  className?: string;
};

function BrandElement({ onPress, type, href, className, children }: PropsWithChildren<BrandElementProps>) {
  if (type === 'button') {
    return (
      <Button variant="none" className={className as TODO} onPress={onPress}>
        {children}
      </Button>
    );
  }
  return (
    <Link variant="none" href={href as TODO} className={className as TODO} onPress={onPress}>
      {children}
    </Link>
  );
}

export function ProductBrands({ id, products = [], placement = 'pdp', camperlab = false, selectBrandProduct }: TProductBrandsProps) {
  const { t } = useI18n();
  const [localId, setLocalId] = useState(id);
  const [emblaRef, emblaAPI] = useEmblaCarousel({
    duration: 25,
    slidesToScroll: 4,
  });
  const { onPrev, onNext, nextBtnDisabled, prevBtnDisabled } = useCarouselControls(emblaAPI);
  let filteredBrandProducts = [];

  if (Array.isArray(products) && products.length > 0) {
    filteredBrandProducts = products?.filter((item) => item.url);
  }

  const hasItems = filteredBrandProducts.length > 0;

  const handleClickBrandProduct = (productBrand: (typeof filteredBrandProducts)[0]) => {
    setLocalId(productBrand.id);
    dataLayerHandleEvent({
      event: 'item_photos',
      event_category: 'product detail',
      event_detail_1: 'open',
      event_detail_2: productBrand.image,
    });

    if (selectBrandProduct && typeof selectBrandProduct === 'function') {
      selectBrandProduct(
        productBrand.id,
        filteredBrandProducts.map((item) => item.id),
      );
    }
  };

  if (!hasItems) return null;

  // const sliderClassName = placement === null ? 'mx-8 lab:mx-2.5' : '';
  // const emblaStyle =
  //   placement === null ?
  //     {
  //       '--slide-height': '4.5rem',
  //       '--slide-gap': getDomainValue({ camper: '0.25rem', nnormal: 'camper', camperlab: '0.625rem' }),
  //     }
  //   : {
  //       '--slide-height': '3rem',
  //       '--slide-gap': '0.2rem',
  //     };

  // El width y height de las imagenes dependiendo de donde se vea el componente
  const camperWH = placement === 'grid' ? 48 : 72;

  const elementType = selectBrandProduct ? 'button' : 'link';

  return (
    <div>
      <p className="mb-2 text-base nnormal:hidden camper:hidden lab:mb-0 lab:text-sm lab:uppercase lab:lg:text-xs">
        {t('ficha.producto', 'colores', 'Colores')}:
      </p>
      <div
        className={placement === 'grid' ? 'overflow-hidden nnormal:ml-0 lab:-mx-2.5' : 'relative -mx-8 overflow-hidden px-8 lab:-mx-2.5 lab:px-2.5 md:hidden'}
      >
        {!nextBtnDisabled && placement === 'grid' ?
          <div
            className={`pointer-events-none absolute z-10 h-full w-full ${camperlab ? 'bg-[linear-gradient(90deg,transparent_60%,#f1efe5)]' : 'bg-[linear-gradient(90deg,transparent_60%,white)]'}`}
          />
        : null}
        <div className="relative" data-square={true} style={{ '--slide-gap': '0.2rem' }} ref={emblaRef}>
          <div className="-ml-[var(--slide-gap)] flex touch-pan-y touch-pinch-zoom">
            {filteredBrandProducts.map((productBrand, index) => (
              <div
                key={`${productBrand.id}-${index}`}
                className={`relative ml-[var(--slide-gap)] min-w-0 shrink-0 grow-0 basis-auto lab:h-15 lab:w-10 ${productBrand.id === localId ? 'after:absolute after:bottom-0 after:block after:h-0.5 after:w-full after:border-b-2 after:border-dark lab:bg-faint lab:after:h-full lab:after:border-0' : ''}`}
              >
                <BrandElement href={productBrand.url} className="cursor-pointer" onPress={() => handleClickBrandProduct(productBrand)} type={elementType}>
                  <span className="sr-only">
                    {productBrand.name} - {productBrand.id}
                  </span>
                  <ProgressiveImg
                    loading={
                      placement === 'grid' ?
                        index < 9 ?
                          'eager'
                        : 'lazy'
                      : 'eager'
                    }
                    src={productBrand.image}
                    alt={`thumbnail image ${index} of ${productBrand.id}`}
                    height={getDomainValue({ camper: camperWH, camperlab: 60, nnormal: camperWH })}
                    width={getDomainValue({ camper: camperWH, camperlab: 40, nnormal: camperWH })}
                    className="object-cover object-bottom text-xxs"
                  />
                </BrandElement>
              </div>
            ))}
          </div>

          {placement === 'grid' ?
            <>
              {!prevBtnDisabled && (
                <button
                  className="absolute left-[0.1rem] top-[calc(50%-16px)] z-20 hidden size-8 cursor-pointer rounded-full bg-[#fff] p-2.5 shadow-[0px_0px_2px_1px_var(--color-disabled-primary)] xl:flex"
                  onClick={() => onPrev()}
                >
                  <ChevronLeftSVG className="size-full" />
                </button>
              )}
              {!nextBtnDisabled && (
                <button
                  className="absolute right-[0.75rem] top-[calc(50%-16px)] z-20 hidden size-8 cursor-pointer rounded-full bg-[#fff] p-2.5 shadow-[0px_0px_2px_1px_var(--color-disabled-primary)] xl:flex"
                  onClick={() => onNext()}
                >
                  <ChevronLeftSVG className="size-full rotate-180" />
                </button>
              )}
            </>
          : null}
        </div>
      </div>
      {placement !== 'grid' ?
        <div className="flex flex-row flex-wrap gap-2.5 lab:gap-1 max-md:hidden md:gap-1">
          {filteredBrandProducts.map((productBrand, index) => (
            <div
              key={`${productBrand.id}-${index}`}
              className={`relative size-18 transition-colors lab:h-15 lab:w-10 lab:hover:bg-faint md:size-15 ${productBrand.id === localId ? 'after:absolute after:bottom-0 after:block after:h-0.5 after:w-full after:border-b-2 after:border-[var(--color-black)] lab:bg-faint lab:after:h-0 lab:after:border-0' : ''}`}
            >
              <BrandElement
                type="link"
                href={productBrand.url}
                className="h-full w-full cursor-pointer [&_img]:h-full [&_img]:w-full [&_picture]:flex [&_picture]:h-full [&_picture]:w-full"
                onPress={() => handleClickBrandProduct(productBrand)}
              >
                <span className="sr-only">
                  {productBrand.name} - {productBrand.id}
                </span>
                <ProgressiveImg
                  src={productBrand.image}
                  alt={`thumbnail image ${index} of ${productBrand.id}`}
                  className={`size-18 object-cover object-bottom text-xxs md:size-15`}
                />
              </BrandElement>
            </div>
          ))}
        </div>
      : null}
    </div>
  );
}
